import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { SearchLg, Plus, Edit01, XCircle, Eye, Mail01, CheckCircle, X } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { shipperQuoteService, generateMockQuoteResponse } from '@/data/mock-quotes';
import { addressBookService } from '@/data/mock-address-book';
import type { ShipperQuoteRequest, ShipperQuoteStatus, CreateShipperQuoteRequestData, UpdateShipperQuoteRequestData } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { storage } from '@/lib/local-storage';
import { ShipperQuoteForm } from '@/components/application/quotes/shipper-quote-form';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';

export const ShipperQuotesPage = () => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const navigate = useNavigate();
    const [quoteRequests, setQuoteRequests] = useState<ShipperQuoteRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ShipperQuoteStatus | ''>('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingRequest, setEditingRequest] = useState<ShipperQuoteRequest | null>(null);
    const [isCheckingForQuotes, setIsCheckingForQuotes] = useState(false);
    const [hasNewQuotes, setHasNewQuotes] = useState(false);
    const [newQuoteRequestId, setNewQuoteRequestId] = useState<string | null>(null);
    const [newQuoteId, setNewQuoteId] = useState<string | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const timerStartedRef = useRef<boolean>(false);

    const shipperId = storage.getItem('accountId') || '1';

    const statusOptions = [
        { id: '', label: t('shipper.list.filters.allStatuses') || 'Tutti gli stati' },
        { id: 'pending', label: t('shipper.status.pending') || 'In attesa' },
        { id: 'responded', label: t('shipper.status.responded') || 'Risposto' },
        { id: 'completed', label: t('shipper.status.completed') || 'Completato' },
        { id: 'cancelled', label: t('shipper.status.cancelled') || 'Annullato' },
    ];

    const getStatusColor = (status: ShipperQuoteStatus) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'responded': return 'success';
            case 'completed': return 'success';
            case 'cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: ShipperQuoteStatus) => {
        const statusMap: Record<ShipperQuoteStatus, string> = {
            'pending': 'shipper.status.pending',
            'responded': 'shipper.status.responded',
            'completed': 'shipper.status.completed',
            'cancelled': 'shipper.status.cancelled',
        };
        return t(statusMap[status]) || status;
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            const requests = shipperQuoteService.getAll(shipperId);
            setQuoteRequests(requests);

            setIsLoading(false);
        };
        loadData();
    }, [shipperId]);

    // Simulazione ricezione quote con timer random
    useEffect(() => {
        if (isLoading) {
            // Reset quando inizia il caricamento
            timerStartedRef.current = false;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        // Avvia il timer solo se ci sono richieste disponibili e non è già stato avviato
        if (!timerStartedRef.current && quoteRequests.length > 0) {
            timerStartedRef.current = true;

            const startQuoteCheck = () => {
                // Timer random tra 5 e 15 secondi
                const randomDelay = 5000 + Math.random() * 10000; // 5-15 secondi in millisecondi

                timerRef.current = setTimeout(() => {
                    setIsCheckingForQuotes(true);

                    // Simula controllo quote (1-2 secondi)
                    setTimeout(() => {
                        // Random booleano per determinare se ci sono nuove quote
                        const hasNewQuote = Math.random() > 0.5;

                        if (hasNewQuote) {
                            // Ricarica le richieste aggiornate
                            const currentRequests = shipperQuoteService.getAll(shipperId);

                            // Trova una richiesta pending o responded che ha ancora recipient disponibili
                            const availableRequests = currentRequests.filter(req =>
                                (req.status === 'pending' || req.status === 'responded')
                            );

                            if (availableRequests.length > 0) {
                                const randomRequest = availableRequests[Math.floor(Math.random() * availableRequests.length)];

                                // Seleziona un recipient random che non ha ancora risposto
                                const respondedRecipientIds = new Set(
                                    randomRequest.receivedQuotes.map(qr => {
                                        const recipient = addressBookService.getAll().find(
                                            entry => entry.email === qr.from && entry.role === 'Freight Forwarder'
                                        );
                                        return recipient?.id;
                                    }).filter(Boolean)
                                );

                                const availableRecipients = randomRequest.recipients.filter(
                                    rid => !respondedRecipientIds.has(rid)
                                );

                                if (availableRecipients.length > 0) {
                                    const randomRecipientId = availableRecipients[Math.floor(Math.random() * availableRecipients.length)];

                                    try {
                                        const newQuote = generateMockQuoteResponse(randomRequest.id, randomRecipientId);
                                        shipperQuoteService.addQuoteResponse(randomRequest.id, newQuote);

                                        // Ricarica i dati
                                        const updatedRequests = shipperQuoteService.getAll(shipperId);
                                        setQuoteRequests(updatedRequests);

                                        setNewQuoteRequestId(randomRequest.id);
                                        setNewQuoteId(newQuote.id);
                                        setHasNewQuotes(true);
                                    } catch (error) {
                                        console.error('Error generating mock quote:', error);
                                    }
                                }
                            }
                        }

                        setIsCheckingForQuotes(false);

                        // Riavvia il timer per il prossimo controllo
                        startQuoteCheck();
                    }, 1000 + Math.random() * 1000); // 1-2 secondi per il controllo
                }, randomDelay);
            };

            startQuoteCheck();
        }

        return () => {
            // Non pulire il timer qui, solo quando il componente viene smontato o quando isLoading diventa true
            // Il timer si riavvia automaticamente dentro startQuoteCheck()
        };
    }, [shipperId, isLoading, quoteRequests.length]); // Aggiungo quoteRequests.length per avviare il timer quando i dati sono pronti

    // Cleanup quando il componente viene smontato
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            timerStartedRef.current = false;
        };
    }, []);

    // Filter quote requests
    const filteredRequests = useMemo(() => {
        let filtered = [...quoteRequests];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(request =>
                request.subject.toLowerCase().includes(term) ||
                request.body.toLowerCase().includes(term)
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(request => request.status === statusFilter);
        }

        // Sort by sent date (newest first)
        filtered.sort((a, b) => b.sentAt.getTime() - a.sentAt.getTime());

        return filtered;
    }, [quoteRequests, searchTerm, statusFilter]);

    const handleCreate = (data: CreateShipperQuoteRequestData | UpdateShipperQuoteRequestData) => {
        if (editingRequest) {
            // Update
            const updated = shipperQuoteService.update(editingRequest.id, data as UpdateShipperQuoteRequestData);
            if (updated) {
                setQuoteRequests(prev => prev.map(req => req.id === updated.id ? updated : req));
            }
        } else {
            // Create
            const newRequest = shipperQuoteService.create(data as CreateShipperQuoteRequestData);
            setQuoteRequests(prev => [newRequest, ...prev]);
        }
        setShowCreateModal(false);
        setEditingRequest(null);
    };

    const handleEdit = (request: ShipperQuoteRequest) => {
        setEditingRequest(request);
        setShowCreateModal(true);
    };

    const getRecipientsInfo = (request: ShipperQuoteRequest) => {
        const recipients = request.recipients.map(id => {
            const entry = addressBookService.getById(id);
            return entry ? `${entry.firstName} ${entry.lastName}` : id;
        });
        return recipients;
    };

    const handleViewNewQuote = () => {
        if (newQuoteRequestId && newQuoteId) {
            // Naviga alla pagina di dettaglio con la nuova quote
            navigate(`/shipper/quotes/${newQuoteRequestId}?quoteId=${newQuoteId}`);
            setHasNewQuotes(false);
            setNewQuoteRequestId(null);
            setNewQuoteId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('shipper.list.loading') || 'Caricamento...'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('title')}</h1>
                    <p className="text-tertiary mt-1">{t('subtitle')}</p>
                </div>
                <Button
                    onClick={() => {
                        setEditingRequest(null);
                        setShowCreateModal(true);
                    }}
                    color="primary"
                    size="md"
                    iconLeading={Plus}
                >
                    {t('shipper.list.createNew') || 'Crea Nuova Richiesta'}
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1">
                    <Input
                        placeholder={t('shipper.list.searchPlaceholder') || 'Cerca richieste...'}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />
                </div>
                <div className="w-full sm:w-64">
                    <Select
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as ShipperQuoteStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Loader per controllo nuove quote */}
            {isCheckingForQuotes && (
                <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <LoadingIndicator type="line-spinner" size="sm" />
                        <span className="text-sm text-tertiary">
                            {t('shipper.detail.checkingQuotes') || 'Controllo nuove quote...'}
                        </span>
                    </div>
                </div>
            )}

            {/* CTA per nuove quote disponibili */}
            {hasNewQuotes && !isCheckingForQuotes && (
                <div className="rounded-xl bg-success-50/10 border border-success-500/20 p-4 shadow-xs ring-1 ring-success-500/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5 text-success-600" />
                            <div>
                                <p className="text-sm font-semibold text-primary">
                                    {t('shipper.detail.newQuotesAvailable') || 'Nuove quote disponibili!'}
                                </p>
                                <p className="text-xs text-tertiary mt-0.5">
                                    {t('shipper.detail.newQuoteReceived') || 'Hai ricevuto una nuova risposta di preventivo'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={handleViewNewQuote}
                                color="primary"
                                size="sm"
                            >
                                {t('shipper.detail.viewNewQuote') || 'Visualizza nuova quote'}
                            </Button>
                            <button
                                onClick={() => {
                                    setHasNewQuotes(false);
                                    setNewQuoteRequestId(null);
                                    setNewQuoteId(null);
                                }}
                                className="text-tertiary hover:text-primary"
                                aria-label={t('common:close') || 'Close'}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quote Requests List */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('shipper.list.title') || 'Richieste di Preventivo Inviate'}
                    description={t('shipper.list.description') || 'Lista delle richieste di preventivo inviate ai freight forwarder'}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('shipper.list.headers.subject') || 'Oggetto'} isRowHeader />
                        <Table.Head label={t('shipper.list.headers.sentAt') || 'Inviata il'} />
                        <Table.Head label={t('shipper.list.headers.recipients') || 'Destinatari'} />
                        <Table.Head label={t('shipper.list.headers.responses') || 'Preventivi Ricevuti'} />
                        <Table.Head label={t('shipper.list.headers.status') || 'Stato'} />
                        <Table.Head label={t('shipper.list.headers.actions') || 'Azioni'} />
                    </Table.Header>
                    <Table.Body>
                        {filteredRequests.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={6} className="text-center text-tertiary py-8">
                                    <Mail01 className="mx-auto h-12 w-12 text-tertiary mb-4" />
                                    <p>{t('shipper.list.noRequests') || 'Nessuna richiesta trovata'}</p>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            filteredRequests.map((request) => {
                                const recipients = getRecipientsInfo(request);
                                return (
                                    <Table.Row key={request.id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">{request.subject}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {request.sentAt.toLocaleDateString('it-IT', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                {recipients.length} {t('shipper.list.recipientsCount') || 'destinatari'}
                                            </div>
                                            <div className="text-xs text-tertiary mt-1">
                                                {recipients.slice(0, 2).join(', ')}
                                                {recipients.length > 2 && ` +${recipients.length - 2}`}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-primary font-medium">
                                                {request.receivedQuotes.length} {t('shipper.list.responsesCount') || 'preventivi'}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={getStatusColor(request.status)} size="sm">
                                                {translateStatus(request.status)}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                {request.status !== 'completed' && request.status !== 'cancelled' && (
                                                    <Button
                                                        type="button"
                                                        onClick={() => handleEdit(request)}
                                                        color="secondary"
                                                        size="sm"
                                                        iconLeading={Edit01}
                                                    >
                                                        {t('shipper.list.edit') || 'Modifica'}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    onClick={() => navigate(`/shipper/quotes/${request.id}`)}
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={Eye}
                                                >
                                                    {t('shipper.list.view') || 'Visualizza'}
                                                </Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })
                        )}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <ModalOverlay isOpen={showCreateModal} onOpenChange={setShowCreateModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-4xl max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {editingRequest
                                            ? (t('shipper.form.editTitle') || 'Modifica Richiesta')
                                            : (t('shipper.form.createTitle') || 'Crea Nuova Richiesta')}
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            setEditingRequest(null);
                                        }}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <ShipperQuoteForm
                                    quoteRequest={editingRequest || undefined}
                                    onSubmit={handleCreate}
                                    onCancel={() => {
                                        setShowCreateModal(false);
                                        setEditingRequest(null);
                                    }}
                                    shipperId={shipperId}
                                />
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

        </div>
    );
};
